import { useEffect, useRef, useState } from 'react';
import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  styled,
} from '@mui/material';
import {
  DialogButton,
  Dropdown,
} from '@beeinventor/dasiot-react-component-lib';
import CrossSvgIcon from '../../components/SvgIcon/CrossSvgIcon';
import AddSvgIcon from '../../components/SvgIcon/AddSvgIcon';
import { parseFirmwareHeader } from '../../utils/fileUtils';

export type FirmwareDeviceType = 'dasloop' | 'dassenses';

export const FIRMWARE_DEVICE_TYPES: Array<{
  id: FirmwareDeviceType;
  name: string;
  value: FirmwareDeviceType;
}> = [
  { id: 'dasloop', name: 'DasLoop', value: 'dasloop' },
  { id: 'dassenses', name: 'DasSensesH', value: 'dassenses' },
];

const Dialog = styled(MuiDialog)`
  & .MuiPaper-root {
    width: 560px;
    max-width: 560px;
    border-radius: 10px;
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.25);
    background-color: #f3f3f3;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

const DialogHeader = styled('div')`
  background: #fff;
  display: flex;
  align-items: center;
  padding-left: 48px;
  padding-right: 8px;
  flex-shrink: 0;

  .title {
    flex: 1;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: 500;
    color: #3e3e3e;
  }

  .close-btn {
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
`;

const DialogBody = styled('div')`
  background: #fff;
  padding: 16px 24px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  .add-btn-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-shrink: 0;
  }

  .add-btn {
    cursor: pointer;
    background: #e6a600;
    border-radius: 4px;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
    height: 40px;
    width: 150px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 16px;
    user-select: none;

    .add-label {
      font-size: 1rem;
      font-weight: 500;
      color: #fff;
    }
  }

  .file-list-label {
    font-size: 0.875rem;
    color: #a1a1a1;
    height: 24px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .file-list-box {
    height: 380px;
    flex-shrink: 0;
    background: #f5f5f5;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    overflow-y: auto;
  }

  .group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .group-header {
    font-size: 0.875rem;
    font-weight: 700;
    color: #333;
    line-height: 20px;
  }

  .file-row {
    background: #fff;
    border-radius: 4px;
    height: 40px;
    display: flex;
    align-items: center;
    overflow: hidden;

    .filename {
      flex: 1;
      font-size: 0.875rem;
      color: #333;
      padding: 0 12px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .remove-btn {
      cursor: pointer;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #a1a1a1;
    }
  }
`;

const DialogFooter = styled('div')`
  background: #fff;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  flex-shrink: 0;
`;

interface ParsedFile {
  file: File;
  firmwareVersion: string;
  hardwareVersion: string;
}

export interface UploadFirmwareGroup {
  firmwareVersion: string;
  files: File[];
}

interface UploadFirmwareDialogProps extends Omit<MuiDialogProps, 'onClose'> {
  onClose?: () => void;
  onUpload?: (groups: UploadFirmwareGroup[]) => void;
  deviceType?: FirmwareDeviceType;
  onDeviceTypeChange?: (type: FirmwareDeviceType) => void;
}

const UploadFirmwareDialog: React.FC<UploadFirmwareDialogProps> = ({
  onClose,
  onUpload,
  deviceType = 'dasloop',
  onDeviceTypeChange,
  ...props
}) => {
  const [parsedFiles, setParsedFiles] = useState<ParsedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!props.open) setParsedFiles([]);
  }, [props.open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (selected.length === 0) return;

    Promise.all(
      selected.map(async (file) => {
        const { firmwareVersion, hardwareVersion } =
          await parseFirmwareHeader(file);
        return { file, firmwareVersion, hardwareVersion };
      }),
    ).then((parsed) => {
      setParsedFiles((prev) => [...prev, ...parsed]);
    });
  };

  const handleRemove = (target: ParsedFile) => {
    setParsedFiles((prev) => prev.filter((pf) => pf !== target));
  };

  const handleAdd = () => {
    fileInputRef.current?.click();
  };

  const groups = Object.entries(
    parsedFiles.reduce<Record<string, ParsedFile[]>>((acc, pf) => {
      (acc[pf.firmwareVersion] ??= []).push(pf);
      return acc;
    }, {}),
  ).map(([firmwareVersion, files]) => ({ firmwareVersion, files }));

  const canUpload = parsedFiles.length > 0;

  const handleUpload = () => {
    onUpload?.(
      groups.map((g) => ({
        firmwareVersion: g.firmwareVersion,
        files: g.files.map(
          (pf) =>
            new File(
              [pf.file],
              `${pf.firmwareVersion}_${pf.hardwareVersion}.fw`,
              { type: pf.file.type },
            ),
        ),
      })),
    );
  };

  const handleClose = () => {
    setParsedFiles([]);
    onClose?.();
  };

  return (
    <Dialog onClose={handleClose} {...props}>
      <DialogHeader>
        <div className="title">Upload New Firmware</div>
        <div className="close-btn" onClick={handleClose}>
          <CrossSvgIcon />
        </div>
      </DialogHeader>

      <DialogBody>
        <div className="add-btn-row">
          <Dropdown
            mode="light"
            sx={{
              display: 'inline-flex',
              maxWidth: '160px',
              paddingTop: 0,
              paddingBottom: 0,
              background: '#f5f5f5',
              border: '1px solid #e5e5e5',
              borderRadius: '4px',
            }}
            selectedId={deviceType}
            list={FIRMWARE_DEVICE_TYPES}
            placeholder="Device Type"
            popperProps={{ disablePortal: true, placement: 'bottom-start' }}
            onSelect={(v) => onDeviceTypeChange?.(v as FirmwareDeviceType)}
          />
          <div className="add-btn" onClick={handleAdd}>
            <span className="add-label">ADD</span>
            <AddSvgIcon sx={{ width: '40px', height: '40px', color: '#fff' }} />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".fw"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        <div className="file-list-label">Upload firmware file list</div>

        <div className="file-list-box">
          {groups.map((group) => (
            <div key={group.firmwareVersion} className="group">
              <div className="group-header">{group.firmwareVersion}</div>
              {group.files.map((pf, i) => (
                <div key={i} className="file-row">
                  <span className="filename">{pf.hardwareVersion}.fw</span>
                  <div className="remove-btn" onClick={() => handleRemove(pf)}>
                    <CrossSvgIcon sx={{ width: '16px', height: '16px' }} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </DialogBody>

      <DialogFooter>
        <DialogButton
          variant="contained"
          color="primary"
          disabled={!canUpload}
          onClick={handleUpload}
          sx={{ fontWeight: 'normal', minWidth: '120px' }}
        >
          UPLOAD
        </DialogButton>
      </DialogFooter>
    </Dialog>
  );
};

export default UploadFirmwareDialog;
